import { useState, useEffect } from 'react'
import { AlertCircle, Building2, CreditCard, Mail, TrendingUp, Users, CalendarCheck, FileText, Info } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { RelativeTime } from '@/components/RelativeTime'
import { Link } from 'react-router-dom'

// shadcn/ui components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { BILLING_STATUS_CONFIG, TRIAL_RISK_CONFIG } from '@/lib/billingStatus'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

import { DashboardDateRangePicker } from '@/components/ui/dashboard-date-range-picker'
import { AdminDashboardService, type AdminDashboardStats } from '@/services/dashboard'
import { GrowthCard } from './GrowthCard'
import { SetupWizardFunnelCard, daysForRange } from './SetupWizardFunnelCard'

const getStatsCards = (stats: AdminDashboardStats | null) => [
    {
        title: 'Total Agencies',
        value: stats?.totalAgencies?.toLocaleString() ?? '—',
        change: stats?.periodComparison?.agenciesChange ?? '',
        trend: stats?.periodComparison?.agenciesChange?.startsWith('+') ? 'up' as const : 'down' as const,
        icon: Building2,
        description: 'Total registered agencies',
        iconBg: 'bg-blue-100 dark:bg-blue-950/50',
        iconColor: 'text-blue-600 dark:text-blue-400',

    },
    {
        title: 'Active Subscriptions',
        value: stats?.activeSubscriptions?.toLocaleString() ?? '—',
        change: '',
        trend: 'up' as const,
        icon: CreditCard,
        description: 'Currently paying customers',
        iconBg: 'bg-emerald-100 dark:bg-emerald-950/50',
        iconColor: 'text-emerald-600 dark:text-emerald-400',

    },
    {
        title: 'Trialing',
        value: stats?.trialing?.toLocaleString() ?? '—',
        change: '',
        trend: 'up' as const,
        icon: TrendingUp,
        description: 'Currently in trial',
        iconBg: 'bg-amber-100 dark:bg-amber-950/50',
        iconColor: 'text-amber-600 dark:text-amber-400',

    },
    {
        title: 'MRR',
        value: stats?.mrr != null ? `$${stats.mrr.toLocaleString()}` : '—',
        change: stats?.periodComparison?.mrrChange ?? '',
        trend: stats?.periodComparison?.mrrChange?.startsWith('+') ? 'up' as const : 'down' as const,
        icon: CreditCard,
        description: 'Monthly Recurring Revenue',
        iconBg: 'bg-indigo-100 dark:bg-indigo-950/50',
        iconColor: 'text-indigo-600 dark:text-indigo-400',
        tooltip: 'Calculated as the total monthly price of all currently active agency subscriptions.',

    },
    {
        title: 'Total Forms',
        value: stats?.totalForms?.toLocaleString() ?? '—',
        change: '',
        trend: 'up' as const,
        icon: FileText,
        description: 'Platform-wide form count',
        iconBg: 'bg-violet-100 dark:bg-violet-950/50',
        iconColor: 'text-violet-600 dark:text-violet-400',

    },
    {
        title: 'Completed Submissions',
        value: stats ? (stats.completedSubmissions ?? stats.totalSubmissions).toLocaleString() : '—',
        change: stats?.periodComparison?.submissionsChange ?? '',
        trend: stats?.periodComparison?.submissionsChange?.startsWith('+') ? 'up' as const : 'down' as const,
        icon: Mail,
        description: 'Recent platform activity',
        iconBg: 'bg-pink-100 dark:bg-pink-950/50',
        iconColor: 'text-pink-600 dark:text-pink-400',

    },
    {
        title: 'Total Leads',
        value: stats?.totalLeads?.toLocaleString() ?? '—',
        change: stats?.periodComparison?.leadsChange ?? '',
        trend: stats?.periodComparison?.leadsChange?.startsWith('+') ? 'up' as const : 'down' as const,
        icon: Users,
        description: 'Recent lead generation',
        iconBg: 'bg-orange-100 dark:bg-orange-950/50',
        iconColor: 'text-orange-600 dark:text-orange-400',

        statsTooltip: ((stats?.completedLeads ?? stats?.totalLeads ?? 0) > 0 || (stats?.partialLeads ?? 0) > 0) ? (
            <div className="flex flex-col gap-1 text-xs">
                <div><span className="font-medium">{(stats?.completedLeads ?? stats?.totalLeads ?? 0).toLocaleString()}</span> completed</div>
                <div><span className="font-medium">{stats?.partialLeads?.toLocaleString() ?? 0}</span> partial</div>
            </div>
        ) : undefined,
    },
    {
        title: 'Appointments',
        value: stats?.appointments?.toLocaleString() ?? '—',
        change: '',
        trend: 'up' as const,
        icon: CalendarCheck,
        description: 'Recent bookings',
        iconBg: 'bg-cyan-100 dark:bg-cyan-950/50',
        iconColor: 'text-cyan-600 dark:text-cyan-400',
        filtered: true,
    },
]

/**
 * Loads one dashboard section on its own. `data` is null until the request
 * returns, `failed` is set when it throws, and `retry` reruns just this
 * request. A response for a superseded date range is dropped.
 */
function useSection<T>(load: () => Promise<T>, deps: unknown[]) {
    const [state, setState] = useState<{ data: T | null; failed: boolean }>({ data: null, failed: false })
    const [attempt, setAttempt] = useState(0)

    useEffect(() => {
        let cancelled = false
        setState({ data: null, failed: false })
        load().then(
            (data) => { if (!cancelled) setState({ data, failed: false }) },
            (error) => {
                console.error('Failed to load dashboard data:', error)
                // Show the failure instead of placeholder numbers, which could be mistaken for real data.
                if (!cancelled) setState({ data: null, failed: true })
            }
        )
        return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, attempt])

    return { ...state, retry: () => setAttempt((n) => n + 1) }
}

const SectionError = ({ onRetry, className = '' }: { onRetry: () => void; className?: string }) => (
    <div className={`flex flex-col items-center justify-center gap-3 py-8 text-sm text-muted-foreground ${className}`}>
        <span className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Couldn't load this section
        </span>
        <Button variant="outline" size="sm" onClick={onRetry}>
            Try again
        </Button>
    </div>
)

const ListSkeleton = () => (
    <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex flex-col gap-1.5">
                    <Skeleton className="h-4 w-[120px]" />
                    <Skeleton className="h-3 w-[80px]" />
                </div>
                <Skeleton className="h-5 w-[50px] rounded-full" />
            </div>
        ))}
    </div>
)

const Dashboard = () => {
    const [dateRange, setDateRange] = useState<string>('1m')
    
    // Every request renders on its own, so the page fills in as responses
    // arrive instead of waiting for the slowest one. Stats, chart, top
    // agencies and the setup wizard funnel are the only requests the date
    // range changes; recent signups and active trials return the same payload
    // whatever the pills say.
    const stats = useSection(() => AdminDashboardService.getStats(dateRange), [dateRange])
    const chart = useSection(() => AdminDashboardService.getChartRows(dateRange), [dateRange])
    const topAgencies = useSection(() => AdminDashboardService.getTopAgencies(dateRange), [dateRange])
    const recentSignups = useSection(() => AdminDashboardService.getRecentSignups(), [])
    const activeTrials = useSection(() => AdminDashboardService.getActiveTrials(), [])
    const setupWizard = useSection(() => AdminDashboardService.getSetupWizardFunnel(daysForRange(dateRange)), [dateRange])

    const statsCards = getStatsCards(stats.data)

    const header = (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
                Dashboard
            </h1>
            
            <DashboardDateRangePicker 
                value={dateRange} 
                onChange={setDateRange}
                tooltipMessage="Filters activity metrics (new agencies, forms, submissions, leads, appointments), trend charts and the setup wizard funnel to the selected period. Snapshot metrics like MRR and active subscriptions always reflect current state."
            />
        </div>
    )

    return (
        <div className="space-y-4 sm:space-y-6 w-full px-4 sm:px-0 animate-in fade-in duration-300">
            {/* Header */}
            {header}

            {/* Stats Cards (8 metrics) */}
            {stats.failed ? (
                <SectionError onRetry={stats.retry} />
            ) : (
            <div className="grid grid-cols-1 xs:grid-cols-2 xl:grid-cols-4 gap-4">
                {statsCards.map((stat, index) => (
                    <Card
                        key={stat.title}
                        className="animate-in fade-in duration-300 py-0"
                        style={{ animationDelay: `${index * 50}ms` }}
                    >
                        <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                                <div className={`flex h-12 w-12 items-center justify-center rounded-lg shrink-0 ${stat.iconBg} ${stat.iconColor}`}>
                                    <stat.icon className="h-6 w-6" />
                                </div>
                                <div className="flex flex-col min-w-0">
                                    {stats.data ? (
                                        <span className="text-2xl font-bold text-foreground leading-tight">
                                            {stat.value}
                                        </span>
                                    ) : (
                                        <Skeleton className="h-7 w-[60px] my-0.5" />
                                    )}
                                    <span className="text-sm text-muted-foreground truncate flex items-center gap-1.5">
                                        {stat.title}
                                        {stat.tooltip && (
                                            <TooltipProvider delayDuration={200}>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Info className="h-3.5 w-3.5 text-muted-foreground/50 hover:text-muted-foreground transition-colors cursor-help" />
                                                    </TooltipTrigger>
                                                    <TooltipContent className="font-normal">
                                                        {stat.tooltip}
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        )}
                                        {stat.statsTooltip && (
                                            <TooltipProvider delayDuration={200}>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Info className="h-4 w-4 text-muted-foreground/70 hover:text-foreground transition-colors cursor-help" />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        {stat.statsTooltip}
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
            )}

            {/* Chart Section */}
            <GrowthCard
                rows={chart.data}
                currentMrr={stats.data?.mrr ?? null}
                error={chart.failed ? <SectionError onRetry={chart.retry} className="h-[320px]" /> : null}
            />

            {/* Setup Wizard Funnel */}
            <SetupWizardFunnelCard
                funnel={setupWizard.data}
                error={setupWizard.failed ? <SectionError onRetry={setupWizard.retry} /> : null}
            />

            {/* Bottom Section: 3 Lists */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Recent Signups */}
                <Card className="py-0 gap-0">
                    <CardHeader className="py-6 gap-0 border-b">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg font-semibold">
                                Recent Signups
                            </CardTitle>
                        </div>
                        <CardDescription>
                            Latest agencies to join the platform
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 pb-6">
                        {recentSignups.failed ? (
                            <SectionError onRetry={recentSignups.retry} />
                        ) : !recentSignups.data ? (
                            <ListSkeleton />
                        ) : recentSignups.data.length > 0 ? (
                            <div className="space-y-2">
                                {recentSignups.data.map((agency) => (
                                    <div
                                        key={agency.id}
                                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-transparent"
                                    >
                                        <div className="flex flex-col min-w-0">
                                            <span className="font-medium text-sm text-foreground truncate">
                                                {agency.name}
                                            </span>
                                            <span className="text-xs text-muted-foreground flex items-center mt-0.5">
                                                <RelativeTime date={agency.createdAt} className="cursor-default" />
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {agency.plan ? (
                                                <Badge variant="secondary" className="font-medium capitalize text-xs shadow-none cursor-default">
                                                    {agency.plan}
                                                </Badge>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">Not selected</span>
                                            )}
                                            {agency.stripeStatus === 'trialing' && (
                                                <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 font-medium capitalize text-xs border-transparent shadow-none cursor-default">
                                                    Trial
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-12 text-center">
                                <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                                    <Building2 className="h-6 w-6 text-muted-foreground" />
                                </div>
                                <h3 className="text-lg font-medium mb-2">No recent signups</h3>
                                <p className="text-sm text-muted-foreground">
                                    No new agencies have joined recently.
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Active Trials */}
                <Card className="py-0 gap-0">
                    <CardHeader className="py-6 gap-0 border-b">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg font-semibold">
                                Active Trials
                            </CardTitle>
                        </div>
                        <CardDescription>
                            Agencies currently evaluating the platform
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 pb-6">
                        {activeTrials.failed ? (
                            <SectionError onRetry={activeTrials.retry} />
                        ) : !activeTrials.data ? (
                            <ListSkeleton />
                        ) : activeTrials.data.length > 0 ? (
                            <div className="space-y-2">
                                {activeTrials.data.map((agency) => (
                                    <div
                                        key={agency.id}
                                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-transparent"
                                    >
                                        <div className="flex flex-col min-w-0 flex-1">
                                            <span className="font-medium text-sm text-foreground truncate">
                                                {agency.name}
                                            </span>
                                            <div className="flex items-center gap-3 mt-0.5">
                                                <span className="text-xs text-muted-foreground truncate">
                                                    {agency.plan || 'Not selected'}
                                                </span>
                                                {agency.trialRisk && TRIAL_RISK_CONFIG[agency.trialRisk.level] && (
                                                    <Tooltip>
                                                        <TooltipTrigger className="cursor-default">
                                                            <Badge tone={TRIAL_RISK_CONFIG[agency.trialRisk.level].tone}>
                                                                {TRIAL_RISK_CONFIG[agency.trialRisk.level].label}
                                                            </Badge>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>{TRIAL_RISK_CONFIG[agency.trialRisk.level].tooltip}</p>
                                                            <p>
                                                                {agency.trialRisk.completedSubmissions} completed submission{agency.trialRisk.completedSubmissions === 1 ? '' : 's'}, {agency.trialRisk.appointments} booking{agency.trialRisk.appointments === 1 ? '' : 's'}
                                                            </p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                )}
                                                {agency.cancelAtPeriodEnd && (
                                                    <Tooltip>
                                                        <TooltipTrigger className="cursor-default">
                                                            <Badge tone={BILLING_STATUS_CONFIG.cancelling.tone}>
                                                                {BILLING_STATUS_CONFIG.cancelling.label}
                                                            </Badge>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>Cancellation scheduled, the trial ends without converting</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0 ml-4">
                                            <Badge
                                                variant="secondary"
                                                className={`font-medium text-xs shadow-none cursor-default ${
                                                    agency.daysRemaining <= 3
                                                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-transparent'
                                                        : agency.daysRemaining <= 7
                                                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-transparent'
                                                        : ''
                                                }`}
                                            >
                                                {agency.daysRemaining <= 0 ? 'Expired' : `${agency.daysRemaining}d left`}
                                            </Badge>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-12 text-center">
                                <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                                    <TrendingUp className="h-6 w-6 text-muted-foreground" />
                                </div>
                                <h3 className="text-lg font-medium mb-2">No active trials</h3>
                                <p className="text-sm text-muted-foreground">
                                    No agencies are currently in trial.
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Top Performing Agencies */}
                <Card className="py-0 gap-0">
                    <CardHeader className="py-6 gap-0 border-b">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg font-semibold">
                                Top Agencies
                            </CardTitle>
                            <Button variant="outline" size="sm" className="text-xs h-7 px-3" asChild>
                                <Link to="/agencies">
                                    View All
                                </Link>
                            </Button>
                        </div>
                        <CardDescription>
                            Ranked by lead volume
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 pb-6">
                        {topAgencies.failed ? (
                            <SectionError onRetry={topAgencies.retry} />
                        ) : !topAgencies.data ? (
                            <ListSkeleton />
                        ) : topAgencies.data.length > 0 ? (
                            <div className="space-y-2">
                                {topAgencies.data.map((agency) => (
                                    <div
                                        key={agency.id}
                                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-transparent"
                                    >
                                        <div className="flex flex-col min-w-0">
                                            <span className="font-medium text-sm text-foreground truncate">
                                                {agency.name}
                                            </span>
                                            <span className="text-xs text-muted-foreground mt-0.5">
                                                {agency.plan || 'Not selected'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-4 text-xs text-muted-foreground shrink-0">
                                            <div className="text-center hidden sm:flex items-center gap-1.5">
                                                <div className="flex items-baseline gap-1">
                                                    <span className="font-semibold text-foreground">{(agency.completedSubmissions ?? agency.submissions).toLocaleString()}</span>
                                                    <span>completed submissions</span>
                                                </div>
                                            </div>
                                            <div className="text-center hidden sm:flex items-center gap-1.5">
                                                <div className="flex items-baseline gap-1">
                                                    <span className="font-semibold text-foreground">{(agency.leads).toLocaleString()}</span>
                                                    <span>leads</span>
                                                </div>
                                                {(((agency.completedLeads ?? agency.leads) > 0) || ((agency.partialLeads ?? 0) > 0)) && (
                                                    <TooltipProvider delayDuration={200}>
                                                        <Tooltip>
                                                            <TooltipTrigger className="cursor-help flex items-center">
                                                                <Info className="h-3.5 w-3.5 text-muted-foreground/70 hover:text-foreground transition-colors" />
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <div className="flex flex-col gap-1 text-xs text-left">
                                                                    <div><span className="font-medium">{(agency.completedLeads ?? agency.leads).toLocaleString()}</span> completed</div>
                                                                    <div><span className="font-medium">{(agency.partialLeads || 0).toLocaleString()}</span> partial</div>
                                                                </div>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-12 text-center">
                                <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                                    <TrendingUp className="h-6 w-6 text-muted-foreground" />
                                </div>
                                <h3 className="text-lg font-medium mb-2">No activity</h3>
                                <p className="text-sm text-muted-foreground">
                                    No agency activity recorded yet.
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>
                
            </div>
        </div>
    )
}

export default Dashboard
